import {useCallback, useContext, useRef, useState} from "react"
import {AuthContext} from "../../../context/Auth.context"
import {ProfileContext} from "../../../context/Profile.context"
import {UIHelperContext} from "../../../context/UIHelper.context"
import {useAPI} from "../../../hooks/useAPI"
import {BackendContext} from "../../../context/BackendContext"
import {useNavigate} from "react-router-dom"
import {getOrbitConfig, getOrbitAddress} from "../../../functions/ipfs-orbit"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faXmark} from "@fortawesome/free-solid-svg-icons"
import styles from "./index.module.scss"


export const NewRepo = () => {
  const {createRepo, healthCheck} = useAPI()
  const navigate = useNavigate()
  const {orbit} = useContext(BackendContext)
  const {profile} = useContext(ProfileContext)
  const {keys} = useContext(AuthContext)
  const {setSnackMessage} = useContext(UIHelperContext)

  const nameInput = useRef<HTMLInputElement>(null)
  const descriptionInput = useRef<HTMLTextAreaElement>(null)
  const tagInput = useRef<HTMLInputElement>(null)
  const [tags, setTags] = useState<string[]>([])

  const onCreateRepo = useCallback(async () => {
    if (!orbit || !profile || !keys) {
      setSnackMessage("IPFS not init yet")
      return
    }

    const orbitConfig = await getOrbitConfig(keys)

    const isHealthy = await healthCheck()
    if (!isHealthy) {
      setSnackMessage("Desktop app not installed")
      return
    }

    const repoName = nameInput.current?.value.trim()
    const repoDescription = descriptionInput.current?.value.trim()

    if (!repoName || !repoDescription) {
      setSnackMessage("Repo name or description is empty")
      return
    }

    const {success, msg, ipfsCID, repoId} = await createRepo(
      repoName,
      repoDescription,
      orbitConfig.accessController as { write: string[] }
    )

    if (success && ipfsCID != null && repoId != null) {
      setSnackMessage("Repo created")
      const address = await getOrbitAddress(orbit, repoId, "keyvalue", orbitConfig)
      const orbitID = address.split("/")[2]
      const profileOrbitId = profile.address.root
      const profilePubKey = profile.address.path
      const repoUrl = `/repos/${profileOrbitId}/${profilePubKey}/${orbitID}/${repoId}/codes`
      navigate(repoUrl)
    } else {
      setSnackMessage(msg ?? "Failed to create repo")
    }
  }, [orbit, keys, profile, createRepo, navigate, setSnackMessage])

  const getTagRow = useCallback(() => {
    return (
      <div className={styles.TagRow}>
        {
          tags.map(t => {
            return (
              <div className={styles.Tag} key={t}>
                <div>{t}</div>
                <FontAwesomeIcon
                  icon={faXmark}
                  className={styles.DeleteTag}
                  onClick={() => setTags(tags.filter(tag => tag !== t))}
                />
              </div>
            )
          })
        }
      </div>
    )
  }, [tags])

  const onClickAddTag = useCallback(() => {
    const tag = tagInput.current?.value.trim().toLowerCase()
    if (!tag || tags.includes(tag)) {
      tagInput.current!.value = ""
      return
    }
    setTags(prevState => [...prevState, tag])
    tagInput.current!.value = ""
  }, [tags, setTags])

  if (!profile) {
    return null
  }

  const profileDisplayName = profile.get("name")

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        Create a new repository
      </div>

      <div className={styles.FormHeader}>
        Repository name
      </div>

      <div className={styles.FormRow}>
        <div>@{profileDisplayName}</div>
        <div>/</div>
        <input className={styles.NameInput} ref={nameInput}/>
      </div>

      <div className={styles.FormHeader}>
        Tags
      </div>

      {
        tags.length > 0 ? getTagRow() : null
      }

      <div className={styles.FormRow}>
        <input className={styles.TagInput}
               ref={tagInput}
               placeholder={"Add a tag here"}
        />
        <button className={styles.AddTagButton}
                onClick={onClickAddTag}
        >
          Add
        </button>
      </div>

      <div className={styles.FormHeader}>
        Description
      </div>

      <textarea className={styles.TextAreaInput}
                ref={descriptionInput}
                placeholder={"Describe your repository"}
      />

      <button className={styles.CreateButton}
              onClick={onCreateRepo}
      >
        Create Repo
      </button>
    </div>
  )
}
